import { useEffect, useRef, useState, useCallback } from "react";
import { devWarn } from "@/lib/log";
import axios from "axios";
import { ChatCircleDots, X, Sparkle } from "@phosphor-icons/react";
import { LeadGateForm } from "@/components/chat/LeadGateForm";
import { MessageThread } from "@/components/chat/MessageThread";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND}/api`;

const storageKey = (productId) => `vendeia_conv_${productId}`;
const leadKey = (productId) => `vendeia_lead_${productId}`;

/**
 * Floating AI chat widget mounted on the public product page.
 *
 *   1. First-time visitor sees a LEAD-GATE: they must enter WhatsApp + email
 *      before any chat happens.  The seller can therefore always recontact.
 *   2. Once submitted, the form is replaced by the messages thread.  The lead
 *      data + the conversation id are persisted in localStorage so a returning
 *      visitor resumes the same thread without re-entering their data.
 *   3. The AI is now scoped to ONLY this product (system prompt enforces it
 *      and uses the seller-provided `especificaciones` field as ground truth).
 */
export default function ChatWidget({ productId, productName, businessName }) {
  const [open, setOpen] = useState(false);
  const [convId, setConvId] = useState(() => {
    try {
      return localStorage.getItem(storageKey(productId)) || null;
    } catch {
      return null;
    }
  });
  const [lead, setLead] = useState(() => {
    try {
      // Lead PII (nombre/teléfono/email) lives in sessionStorage only: it
      // prefills the gate form within the tab but never persists on disk.
      const raw = sessionStorage.getItem(leadKey(productId));
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  });
  const [conv, setConv] = useState(null);
  const [text, setText] = useState("");
  // Lead-gate form fields
  const [name, setName] = useState(() => lead?.name || "");
  const [phone, setPhone] = useState(() => lead?.phone || "");
  const [email, setEmail] = useState(() => lead?.email || "");
  const [gateError, setGateError] = useState("");
  const [gateBusy, setGateBusy] = useState(false);
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  const persistConvId = useCallback(
    (id) => {
      try {
        if (id) localStorage.setItem(storageKey(productId), id);
        else localStorage.removeItem(storageKey(productId));
      } catch {
        /* ignore */
      }
    },
    [productId],
  );

  // Auto-scroll to last message
  useEffect(() => {
    if (scrollRef.current && conv?.messages?.length) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [conv?.messages?.length, open]);

  // Hydrate existing conversation on open
  useEffect(() => {
    if (!open || !convId || conv) return;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/public/conversations/${convId}`);
        setConv(data);
      } catch (e) {
        // Stale conv id (deleted, etc.) — clear and start fresh
        persistConvId(null);
        setConvId(null);
      }
    })();
  }, [open, convId, conv, persistConvId]);

  const submitLeadGate = async (e) => {
    e?.preventDefault?.();
    setGateError("");
    const cleanPhone = phone.trim();
    const cleanEmail = email.trim().toLowerCase();
    if (!cleanPhone || cleanPhone.replace(/\D/g, "").length < 7) {
      setGateError("Ingresa un WhatsApp válido (mínimo 7 dígitos).");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(cleanEmail)) {
      setGateError("Ingresa un email válido.");
      return;
    }
    setGateBusy(true);
    try {
      const { data } = await axios.post(`${API}/conversations/start`, {
        product_id: productId,
        visitor_name: name.trim(),
        visitor_phone: cleanPhone,
        visitor_email: cleanEmail,
      });
      setConv(data);
      setConvId(data.id);
      persistConvId(data.id);
      const leadObj = { name: name.trim(), phone: cleanPhone, email: cleanEmail };
      setLead(leadObj);
      try {
        sessionStorage.setItem(leadKey(productId), JSON.stringify(leadObj));
      } catch {
        /* ignore */
      }
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        "No se pudo iniciar la conversación. Inténtalo de nuevo.";
      setGateError(typeof msg === "string" ? msg : "Error al iniciar.");
    } finally {
      setGateBusy(false);
    }
  };

  const sendMessage = async (e) => {
    e?.preventDefault?.();
    const value = text.trim();
    if (!value || sending || !convId) return;
    setSending(true);
    setText("");
    const optimistic = {
      role: "visitor",
      text: value,
      created_at: new Date().toISOString(),
    };
    setConv((c) => ({
      ...(c || {}),
      messages: [...(c?.messages || []), optimistic],
    }));
    try {
      const { data } = await axios.post(`${API}/conversations/${convId}/message`, {
        text: value,
        visitor_name: lead?.name || "",
        visitor_phone: lead?.phone || "",
        visitor_email: lead?.email || "",
      });
      setConv(data);
    } catch (err) {
      devWarn("send message failed", err);
      setConv((c) => ({
        ...(c || {}),
        messages: (c?.messages || []).filter((m) => m !== optimistic),
      }));
      setText(value);
    } finally {
      setSending(false);
      inputRef.current?.focus();
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const hasUnread =
    !open && conv?.messages?.length > 0 && conv.messages[conv.messages.length - 1]?.role !== "visitor";

  // Lead-gate is the first screen unless the visitor already started a
  // conversation in a previous session (convId persists in localStorage).
  const showGate = !convId;

  return (
    <>
      {!open && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="fixed bottom-20 right-5 z-50 inline-flex items-center gap-2 px-4 py-3 bg-klein text-white shadow-lg hover:bg-klein-dark transition-all rounded-full"
          data-testid="chat-widget-open"
        >
          <ChatCircleDots size={22} weight="fill" />
          <span className="text-sm font-semibold hidden sm:inline">¿Tienes una pregunta?</span>
          {hasUnread && (
            <span className="absolute -top-1 -right-1 w-3 h-3 bg-[#FF3D00] rounded-full ring-2 ring-white" />
          )}
        </button>
      )}

      {open && (
        <div className="fixed inset-x-0 bottom-0 sm:inset-auto sm:bottom-20 sm:right-5 z-50 sm:w-[380px] max-h-[88vh] sm:max-h-[640px] flex flex-col bg-white shadow-2xl border border-zinc-200 sm:rounded-lg overflow-hidden">
          <div className="px-4 py-3 bg-klein text-white flex items-center justify-between">
            <div className="flex items-center gap-2 min-w-0">
              <Sparkle size={18} weight="fill" />
              <div className="min-w-0">
                <div className="font-semibold text-sm truncate">Asistente · {businessName || "Tienda"}</div>
                <div className="text-[11px] text-white/80 truncate">
                  Pregúntame sobre <span className="font-medium">{productName}</span>
                </div>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="p-1.5 hover:bg-white/10 rounded"
              data-testid="chat-widget-close"
            >
              <X size={18} />
            </button>
          </div>

          {showGate ? (
            <LeadGateForm
              businessName={businessName}
              name={name}
              setName={setName}
              phone={phone}
              setPhone={setPhone}
              email={email}
              setEmail={setEmail}
              gateError={gateError}
              gateBusy={gateBusy}
              onSubmit={submitLeadGate}
            />
          ) : (
            <MessageThread
              conv={conv}
              sending={sending}
              text={text}
              setText={setText}
              onSend={sendMessage}
              onKeyDown={handleKeyDown}
              scrollRef={scrollRef}
              inputRef={inputRef}
            />
          )}
        </div>
      )}
    </>
  );
}
